import React from 'react'
import styled, { css } from 'styled-components'
import {
    Button,
    HamburgerButton,
    Logo,
    LogoLink,
    Menu,
    NavDropdown,
    NavProvider,
    NavLink,
} from '@streamr/streamr-layout'
import Link from '~shared/Link'
import Display from '~shared/Display'
import Navbar from '~shared/Navbar'
import urls from '~utils/urls'
import usePosts from '~hooks/usePosts'

export const DISCOVER = 'Discover'

export const PROJECT = 'Project'

export const DEVELOPERS = 'Developers'

export const AppsAndServicesGroup = () => (
    <Menu.Group name="Tech and apps">
        <Menu.Item as={Link} to={urls.discover.network}>
            Network
        </Menu.Item>
        <Menu.Item as={Link} to={urls.discover.dataToken}>
            DATA Token
        </Menu.Item>
        <Menu.Item as={Link} to={urls.discover.dataUnions}>
            Data Unions
        </Menu.Item>
        <Menu.Item as={Link} to={urls.discover.marketplace}>
            Marketplace
        </Menu.Item>
        {!!process.env.GATSBY_VISUALIZER && (
            <Menu.Item as={Link} to="/visualizer/">
                Network Visualizer
            </Menu.Item>
        )}
    </Menu.Group>
)

export const CaseStudiesGroup = () => (
    <Menu.Group name="Case studies">
        <Menu.Item as={Link} to={urls.caseStudies.paveMotors}>
            Pave Motors
        </Menu.Item>
        <Menu.Item as={Link} to={urls.caseStudies.swash}>
            Swash
        </Menu.Item>
        <Menu.Item as={Link} to={urls.caseStudies.tracey}>
            Tracey
        </Menu.Item>
        <Menu.Item as={Link} to={urls.caseStudies.machineWitness}>
            Machine Witness
        </Menu.Item>
    </Menu.Group>
)

export const ProjectMenu = () => (
    <Menu>
        <Menu.Item as={Link} to={urls.project.about}>
            About
        </Menu.Item>
        <Menu.Item as={Link} to={urls.project.tokenMigration}>
            Token Migration
        </Menu.Item>
        <Menu.Item as={Link} to={urls.project.ecosystem}>
            Ecosystem
        </Menu.Item>
        <Menu.Item as={Link} to={urls.project.papers}>
            Papers
        </Menu.Item>
        <Menu.Item as={Link} to={urls.project.roadmap}>
            Roadmap
        </Menu.Item>
        <Menu.Item as={Link} href={urls.community.blog} newTab>
            Blog
        </Menu.Item>
    </Menu>
)

export const DevelopersMenu = () => (
    <Menu>
        <Menu.Item as={Link} href={urls.docs.index}>
            Docs
        </Menu.Item>
        <Menu.Item as={Link} href={urls.developers.dataFund}>
            Data Fund
        </Menu.Item>
        <Menu.Item as={Link} to={urls.developers.design}>
            Design assets
        </Menu.Item>
    </Menu>
)

const BlogPostItem = styled(Menu.SecondaryItem)`
    max-width: 136px;
    overflow: hidden;
    text-overflow: ellipsis;
`

const UseCoreButtonAttrs = ({ theme }) => ({
    tag: 'a',
    kind: theme.light ? 'secondary' : 'primary',
    variant: !!theme.light && 'light',
    outline: true,
    href: '/core',
    children: 'Use Core',
})

const UseCoreButton = styled(Button).attrs(UseCoreButtonAttrs)`
    height: 32px;
    padding: 0 16px !important;

    ${({ theme }) => !theme.light && css`
        :visited {
            color: #0324ff;
            border-color: #0324ff;
        }
    `}
`

const UnstyledNav = ({ className, light, afterLogoComponent }) => {
    const posts = usePosts()

    const { highlight: current } = NavProvider.useState()

    return (
        <div className={className}>
            <Navbar>
                <Navbar.Item spread={!afterLogoComponent}>
                    <LogoLink href="/">
                        <Logo />
                    </LogoLink>
                </Navbar.Item>
                {afterLogoComponent}
                <Display as={Navbar.Item} mobile="none" desktop="block">
                    <NavDropdown
                        highlight={current === DISCOVER}
                        toggle={(
                            <NavLink>
                                {DISCOVER}
                            </NavLink>
                        )}
                        menu={(
                            <Menu>
                                <AppsAndServicesGroup />
                                <CaseStudiesGroup />
                            </Menu>
                        )}
                    />
                </Display>
                <Display as={Navbar.Item} mobile="none" desktop="block">
                    <NavDropdown
                        highlight={current === PROJECT}
                        toggle={(
                            <NavLink>
                                {PROJECT}
                            </NavLink>
                        )}
                        menu={(
                            <ProjectMenu />
                        )}
                    />
                </Display>
                <Display as={Navbar.Item} mobile="none" desktop="block">
                    <NavDropdown
                        highlight={current === DEVELOPERS}
                        toggle={(
                            <NavLink>
                                {DEVELOPERS}
                            </NavLink>
                        )}
                        menu={(
                            <DevelopersMenu />
                        )}
                    />
                </Display>
                <Display as={Navbar.Item} mobile="none" desktop="block">
                    <NavDropdown
                        toggle={(
                            <NavLink>
                                Community
                            </NavLink>
                        )}
                        menu={(
                            <Menu>
                                <Menu.Group>
                                    <Menu.Item as={Link} newTab href={urls.community.discord}>
                                        Discord
                                    </Menu.Item>
                                    <Menu.Item as={Link} newTab href={urls.community.twitter}>
                                        Twitter
                                    </Menu.Item>
                                </Menu.Group>
                                {posts.length > 0 && (
                                    <Menu.Group name="From the blog">
                                        {posts.map(({ id, url, title }) => (
                                            <BlogPostItem as={Link} newTab href={url} key={id}>
                                                {`→ ${title}`}
                                            </BlogPostItem>
                                        ))}
                                    </Menu.Group>
                                )}
                            </Menu>
                        )}
                    />
                </Display>
                <Display as={Navbar.Item} mobile="none" desktop="block">
                    <div
                        css={`
                            padding-left: 8px;
                        `}
                    >
                        <UseCoreButton theme={{
                            light,
                        }}
                        />
                    </div>
                </Display>
                <Display as={Navbar.Item} desktop="none">
                    <HamburgerButton idle />
                </Display>
            </Navbar>
        </div>
    )
}

const Nav = styled(UnstyledNav)`
    ${({ light }) => !!light && css`
        color: #FFFFFF;
    `}
`

export default Nav
